import { z } from 'zod'
import { SignupPath } from '@lb/domain'

/**
 * A plan chosen before the funnel opened — the public website's « Choisir … »
 * buttons link here as `/signup?audience=PRO&plan=practitioner` (client
 * request, 2 Oct 2026), so the visitor lands on « À propos de vous » with the
 * plan already picked instead of choosing it a second time.
 *
 * Every field is optional and anything malformed is dropped rather than
 * refused: a stale or hand-edited link must still open the funnel, at worst on
 * the plan cards. The audience is accepted in any case (`pro`, `PRO`).
 */
export const signupSearchSchema = z.object({
  audience: z
    .string()
    .optional()
    .transform((value) => {
      const parsed = SignupPath.safeParse(value?.toUpperCase())
      return parsed.success ? parsed.data : undefined
    })
    .catch(undefined),
  plan: z
    .string()
    .max(80)
    .regex(/^[a-z0-9-]+$/)
    .optional()
    .catch(undefined),
  seats: z.coerce.number().int().min(1).max(5000).optional().catch(undefined),
})
export type SignupIntent = z.infer<typeof signupSearchSchema>

/**
 * What the funnel does with an intent once the audience's catalogue is known:
 * open the account step on a plan that exists, or the plan cards on the right
 * tab when the key is unknown (renamed plan, old link) — never a broken step.
 */
export function resolveIntent(
  intent: SignupIntent,
  catalogue: readonly { key: string }[],
): { step: 'account'; path: SignupPath; planKey: string } | { step: 'landing'; path: SignupPath } | null {
  if (!intent.audience && !intent.plan) return null
  const path = intent.audience ?? 'PARENT'
  const plan = intent.plan ? catalogue.find((one) => one.key === intent.plan) : undefined
  return plan ? { step: 'account', path, planKey: plan.key } : { step: 'landing', path }
}
